// components/QuestionCard.tsx — ported from docs/specs/design-2026-04-21/messages.jsx
// Source functions: QuestionBody + VeroFalsoReplies

import { useState, type JSX } from 'react';
import { useTokens } from '../state/theme';
import { ProfMessage } from './ProfMessage';

// Design decision: image_ref is passed through directly from the backend as a relative path.
// It may be "img_sign_by_hash/HASH.png" (deduplicated) or "img_sign/PLAIN.png" (plain form).
// This component prepends "/" and renders the image — no path manipulation needed here.
//
// TODO(deploy): copy ai-tutor/assets/img_sign/ AND ai-tutor/assets/img_sign_by_hash/ into
// ai-tutor/frontend/public/ so that Vite serves them in dev and deploy.
// The copy-sign-images.js script handles both folders.

export interface Question {
  id: string;
  text: string;
  argomentoName: string;   // e.g. "Segnaletica di pericolo"
  imageRef?: string | null;   // full relative path from backend ("img_sign/NN.png" OR "img_sign_by_hash/HASH.png")
}

export type VFAnswer = 'Vero' | 'Falso';

export interface QuestionCardProps {
  question: Question;
  qIndex: number;
  qTotal?: number;
  answered: boolean;
  onAnswer?: (answer: VFAnswer) => void;
  /** W4/L3b T3: when true, render a small "Domanda ostica" pill above the
   *  question text — signals to the student that the Prof knows this rule
   *  has been tricky for them. Soft acknowledgment, not punitive framing. */
  isLeech?: boolean;
  /** W4/L3b T9: when true, render a "Confronto mirato" pill above the
   *  question text — signals an injected contrastive pair after a confusable
   *  miss. Spec §2 / Phase 2. */
  isContrastivePair?: boolean;
}

interface VeroFalsoRepliesProps {
  onPick: (answer: VFAnswer) => void;
}

/** Reply pill row — also exported so other screens can reuse it independently. */
export function VeroFalsoReplies({ onPick }: VeroFalsoRepliesProps): JSX.Element {
  const t = useTokens();
  // Same leak class as VeroFalsoPills (founder phone bug 2026-07-08): hover must
  // be React state, never a DOM mutation, or a touch-tap's mouseover residue
  // survives React's style diff onto the next render of a reused node.
  const [hovered, setHovered] = useState<VFAnswer | null>(null);
  const opts: { v: VFAnswer; hint: string }[] = [
    { v: 'Vero',  hint: 'l\'affermazione è corretta' },
    { v: 'Falso', hint: 'l\'affermazione è sbagliata' },
  ];
  return (
    <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {opts.map((o) => {
        const isHovered = hovered === o.v;
        return (
          <button
            key={o.v}
            onClick={() => {
              setHovered(null);
              onPick(o.v);
            }}
            style={{
              background: isHovered ? t.accentSoft : t.surface,
              border: `1px solid ${isHovered ? t.accent : t.lineStrong}`,
              borderRadius: t.radius.pill, padding: '9px 16px',
              cursor: 'pointer', color: t.text,
              fontFamily: t.fonts.body, fontSize: 14, fontWeight: 500,
              letterSpacing: -0.05,
              display: 'inline-flex', alignItems: 'center', gap: 8,
              transition: 'background .15s, border-color .15s',
            }}
            onMouseEnter={() => setHovered(o.v)}
            onMouseLeave={() => setHovered((h) => (h === o.v ? null : h))}
          >
            <span>{o.v}</span>
            <span style={{ color: t.textFaint, fontSize: 11.5, fontWeight: 400 }}>
              · {o.hint}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Question presented as a prof bubble with optional sign image and V/F reply pills. */
export function QuestionCard({
  question,
  qIndex,
  qTotal,
  answered,
  onAnswer,
  isLeech,
  isContrastivePair,
}: QuestionCardProps): JSX.Element {
  const t = useTokens();
  return (
    <ProfMessage>
      {/* Question counter + argomento name */}
      <div style={{
        fontFamily: t.fonts.mono, fontSize: 10, letterSpacing: 0.8,
        color: t.textFaint, textTransform: 'uppercase', marginBottom: 10,
      }}>
        {qTotal != null ? `Domanda ${qIndex} di ${qTotal} · ` : ''}{question.argomentoName}
      </div>

      {/* W4/L3b T3 + T9: optional context pills above the question. Both are
          soft acknowledgments — never punitive ("ostica" = "tricky" not "you
          keep failing"). Stack vertically when both fire. */}
      {(isLeech || isContrastivePair) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          {isContrastivePair && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '3px 9px', borderRadius: t.radius.pill,
              background: t.accentSoft, color: t.accent,
              border: `1px solid ${t.accent}`,
              fontFamily: t.fonts.mono, fontSize: 10, fontWeight: 600,
              letterSpacing: 0.5, textTransform: 'uppercase',
            }}>
              Confronto mirato
            </span>
          )}
          {isLeech && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '3px 9px', borderRadius: t.radius.pill,
              background: 'rgba(255, 165, 0, 0.12)', color: t.accent,
              border: `1px solid ${t.lineStrong}`,
              fontFamily: t.fonts.mono, fontSize: 10, fontWeight: 600,
              letterSpacing: 0.5, textTransform: 'uppercase',
            }}>
              Domanda ostica
            </span>
          )}
        </div>
      )}

      {/* Optional road-sign image (real PNG served from Vite public folder) */}
      {question.imageRef && (
        <div style={{ marginBottom: 14, maxWidth: 220, minHeight: 120, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img
            src={`/${question.imageRef}`}
            alt="Segnale stradale"
            style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
            decoding="async"
          />
        </div>
      )}

      {/* Question text */}
      <div style={{
        fontFamily: t.fonts.serif, fontSize: 18, lineHeight: 1.45,
        color: t.text, letterSpacing: -0.1,
      }}>
        {question.text}
      </div>

      {/* Reply pills — hidden once answered */}
      {!answered && onAnswer && (
        <VeroFalsoReplies onPick={onAnswer} />
      )}
    </ProfMessage>
  );
}
